import {
  Divider,
  MenuItem as MuiMenuItem,
  Popover,
  type PopoverProps,
  styled,
  Typography,
} from "@material-ui/core";
import { useIntl } from "react-intl";

export type MenuItem = {
  id: string;
  defaultMessage: string;
  icon: JSX.Element | null;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  textStyle?: React.CSSProperties;
  subText?: string;
};

const MenuContainer = styled("ul")({
  margin: 0,
  padding: 0,
});

const Option = styled(MuiMenuItem)(({ theme }) => ({
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 3),
  // a little more breathing room at the top/bottom of the list
  "&:first-of-type": { paddingTop: theme.spacing(2) },
  "&:last-of-type": { paddingBottom: theme.spacing(2) },
}));

const IconWrapper = styled("div")({
  width: 24,
  height: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const TextColumn = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
});

const SubText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[700],
}));

const AlmostFullWidthDivider = styled(Divider)({
  width: "90%",
  margin: "0 auto",
});

export const PopoverMenu = ({
  anchorEl,
  anchorOrigin = { vertical: "bottom", horizontal: "right" },
  transformOrigin = { vertical: -4, horizontal: "right" },
  open,
  onClose,
  primaryMenuItems,
  secondaryMenuItems,
  ...restProps
}: {
  anchorEl: HTMLElement;
  anchorOrigin?: {
    vertical: number | "bottom" | "top";
    horizontal: "left" | "right";
  };
  transformOrigin?: {
    vertical: number | "bottom" | "top";
    horizontal: "left" | "right";
  };
  open: boolean;
  onClose: () => void;
  primaryMenuItems: MenuItem[];
  secondaryMenuItems?: MenuItem[];
} & PopoverProps) => {
  const { formatMessage } = useIntl();

  const renderOption = ({
    id,
    defaultMessage,
    icon,
    onClick,
    textStyle,
    subText,
  }: MenuItem) => (
    <Option key={id} onClick={onClick}>
      {icon && <IconWrapper>{icon}</IconWrapper>}
      <TextColumn>
        <Typography variant="body1" style={textStyle}>
          {formatMessage({ id, defaultMessage })}
        </Typography>
        {subText && <SubText variant="body2">{subText}</SubText>}
      </TextColumn>
    </Option>
  );

  return (
    <Popover
      disableScrollLock
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      {...restProps}
    >
      <MenuContainer>
        {primaryMenuItems.map(renderOption)}
        {!!secondaryMenuItems?.length && (
          <>
            <AlmostFullWidthDivider />
            {secondaryMenuItems.map(renderOption)}
          </>
        )}
      </MenuContainer>
    </Popover>
  );
};
